import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import Quill from 'quill'
import api from 'api/api'
import useStore, { type IKnovStore } from 'state/knovStore'
import { ensureSpaceUsersLoaded } from 'state/spaceUsers'
import { getConfiguredAgentModels, getAgentModelMentionDetails } from 'lib/agentModels'
import { map, uniq, debounce, defer, partial, cloneDeep } from 'lodash'
import {
    KnovClipboard,
    dataUrlToBlob,
    normalizeExistingDelta,
} from 'components/shared/quill/quillHelpers'
import { isEqual } from 'lodash'
import questModel from 'components/quests/questModel'
import styles from './mentions.module.scss'
import { isIMEComposing } from 'state/imperativeApis/editorApi'
import { isEmpty, containsModelMention, createModelMention } from 'lib/value'
import { isIOS, isMobile, isAndroid } from 'lib/device'
import cn from 'classnames'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import { getCenterIndex, getSwiperRef } from 'state/imperativeApis/swiperApi'
import { generateFingerprint } from '../../utils/index'
import { extractStreamEmbedConfigs } from 'components/shared/LinkPreview'
import { getKnovEmbedTargetKeys, resolveKnovEmbedLinks } from 'components/answers/knovEmbedLinks'
import TiptapEditor from './TiptapEditor'
import type { ComposerKind, OperationTrace } from 'utils/operationDiagnostics'
import useUserHasFeature from '../../refactor/hooks/useUserHasFeature'
import { buildActionCommandAutocompleteItems } from './tiptap/actionCommandAutocompleteSource'
import type { IAiModel } from 'state/aiModels'
import type { User } from 'types/users'
import { getReadOnlyShowableValue } from './readOnlyTrailingLinks'

const EMPTY_EDITOR_DATA = []
const MENTION_USERS_REFRESH_COOLDOWN_MS = 1500

type UserMention = User & {
    value: string
    content: string
    link: string
    isModel: false
}

// Add a global tracker for keydown events
let lastKeyPressed = ''

// CommonEditor props are passed through from many call sites in the app,
// making a strict interface impractical. We type the ref as a generic object.
const CommonEditor = React.forwardRef(
    (
        props: Record<string, unknown> & {
            readOnly?: boolean
            defaultValue?: unknown
            type?: string
            editMode?: boolean
            quest?: Record<string, unknown> & {
                private?: boolean
                team_id?: string | number | null
                team?: { id: string | number; public?: boolean }
            }
            globalAgentModel?: string | null
            setAgentModel?: (model: string | null) => void
            placeholder?: string
            onChange?: (delta: unknown) => void
            onFocus?: (editor: unknown) => void
            onBlur?: (editor: unknown) => void
            onEditorLoad?: (editor: unknown) => void
            imgHandler?: (event: unknown) => void
            videoHandler?: (video: File) => void
            postHandler?: () => void
            setLinkPreviews?: (links: string[]) => void
            setEmbeds?: (embeds: unknown[], diagnostic?: OperationTrace | null) => void
            diagnosticComposer?: ComposerKind
            diagnosticComposerId?: string
            setEmbedsLoading?: (loading: boolean, promise?: Promise<unknown[]>) => void
            setStreamEmbeds?: (embeds: unknown[]) => void
            setStreamEmbedsLoading?: (loading: boolean) => void
            linkPreviewsFingerprint?: string
            embedsFingerprint?: string
            streamEmbedsFingerprint?: string
            answer?: {
                is_transcription?: boolean
                mp4_recording_url?: string
                recording_url?: string
                [key: string]: unknown
            }
            isEmbed?: boolean
            collapseEnabled?: boolean
            collapseMaxChars?: number
            collapseInstanceId?: string
            collapseShowMoreControl?: boolean
            uiId?: string
            onActionTokenPromptChange?: (payload: {
                attrs: Record<string, unknown>
                index: number
                promptContent: string
                committed?: boolean
                removed?: boolean
            }) => void
            getActionTokenReferenceMentions?: (
                attrs: Record<string, unknown>,
            ) => Array<Record<string, unknown>>
            readOnlyActionTokenPrompts?: boolean
            aiModels?: IAiModel[]
            editorTestId?: string
            userMentionsOnly?: boolean
        },
        ref: React.ForwardedRef<unknown>,
    ) => {
        const edit = props?.readOnly ? '' : 'edit'
        // Editor wrapper ref - holds a TiptapEditorRef-like object with Quill-compat methods.
        // Typed loosely because it's assigned dynamically from onEditorLoad.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const editorRef = useRef<any>(null)
        const programmaticChangeRef = useRef(false)
        const [editorLoaded, setEditorLoaded] = useState(false)

        // Simple setter helper, defined early so it's available in nested functions
        function programmaticChangeFlagSetter(val: boolean) {
            programmaticChangeRef.current = val
        }

        // Helper function to get the current editor instance (Quill or Tiptap)
        const getEditor = () => editorRef.current

        // Helper function specifically for Quill editor operations

        // Helper function specifically for Tiptap editor operations
        const getTiptapEditor = () => {
            return editorRef.current
        }

        // Compute the default value for the uncontrolled editor
        const defaultValue = useMemo(
            () => normalizeExistingDelta(props.defaultValue),
            [props.defaultValue],
        )
        const previousDefaultValueRef = useRef(defaultValue)

        // Track previous model state to detect changes, and processing flag
        const isProcessingModelRef = useRef(false)
        const prevModelRef = useRef(props.globalAgentModel)
        const hasInitializedRef = useRef(false)
        const editorCreationTimeRef = useRef(Date.now()) // Track when editor was created

        // Replace the link preview refs with a single debounced function ref
        const handleLinksRef = useRef(null)
        // Add debounced reference for KnovQuest links
        const handleKnovQuestLinksRef = useRef(null)
        // Add debounced reference for Stream links
        const handleStreamLinksRef = useRef(null)

        // Add type for mention object
        interface ModelMention {
            denotationChar: string
            id: string
            value: string
            type: string
            link: string
        }

        const needsEditableEditorData =
            !props.readOnly || Boolean(props.globalAgentModel || props.setAgentModel)
        const filter =
            needsEditableEditorData && props.quest ? questModel.getFilter(props.quest) : null
        const selectStoreAiModels = useCallback(
            (state: IKnovStore) =>
                needsEditableEditorData ? state.aiModels || EMPTY_EDITOR_DATA : EMPTY_EDITOR_DATA,
            [needsEditableEditorData],
        )
        const selectStoreActionDefinitions = useCallback(
            (state: IKnovStore) =>
                needsEditableEditorData
                    ? state.actionDefinitions || EMPTY_EDITOR_DATA
                    : EMPTY_EDITOR_DATA,
            [needsEditableEditorData],
        )
        const selectAgentModelConfig = useCallback(
            (state: IKnovStore) => (needsEditableEditorData ? state.agentModelConfig : null),
            [needsEditableEditorData],
        )
        const selectPublicTeams = useCallback(
            (state: IKnovStore) =>
                needsEditableEditorData
                    ? state.publicTeams || EMPTY_EDITOR_DATA
                    : EMPTY_EDITOR_DATA,
            [needsEditableEditorData],
        )
        const selectStoreUsers = useCallback(
            (state: IKnovStore) =>
                needsEditableEditorData ? state.users || EMPTY_EDITOR_DATA : EMPTY_EDITOR_DATA,
            [needsEditableEditorData],
        )
        const storeAiModels = useStore(selectStoreAiModels)
        const storeActionDefinitions = useStore(selectStoreActionDefinitions)
        const agentModelConfig = useStore(selectAgentModelConfig)
        const publicTeams = useStore(selectPublicTeams) || EMPTY_EDITOR_DATA
        const questTeam = props.quest?.team
        const filterTeamId = filter?.team_ids?.[0]
        const normalizedTeamId = filterTeamId != null ? String(filterTeamId) : null
        const questTeamId = questTeam ? String(questTeam.id) : null
        const questTeamIsPublic = questTeam?.public

        const restrictMentionsToTeam = useMemo(() => {
            if (!normalizedTeamId) return false

            if (questTeamId && questTeamId === normalizedTeamId && questTeamIsPublic) {
                return false
            }

            const matchingPublicTeam = publicTeams.some(
                team => String(team.id) === normalizedTeamId && team.public,
            )

            return !matchingPublicTeam
        }, [normalizedTeamId, questTeamId, questTeamIsPublic, publicTeams])

        const rawUsers = useStore(selectStoreUsers)

        // Mention suggestions read the space user list, which no longer loads at boot.
        // Start the load when an editable composer gains focus, ahead of the first '@'.
        const { onFocus: onFocusProp } = props
        const handleEditorFocus = useCallback(
            (editor: unknown) => {
                if (needsEditableEditorData) ensureSpaceUsersLoaded()
                onFocusProp?.(editor)
            },
            [needsEditableEditorData, onFocusProp],
        )

        const buildUserMentions = useCallback(
            (sourceUsers: User[] | null | undefined): UserMention[] =>
                (sourceUsers || [])
                    .filter(user => {
                        if (restrictMentionsToTeam && normalizedTeamId) {
                            const teamIds = (user?.space_team_ids || []).map(id => String(id))
                            return teamIds.includes(normalizedTeamId)
                        }
                        return true
                    })
                    .map(user => {
                        return {
                            ...user,
                            value: user.name,
                            content: user.name,
                            link: `/users/${user.name}`,
                            isModel: false,
                        }
                    }),
            [normalizedTeamId, restrictMentionsToTeam],
        )

        const userMentions = useMemo(
            () => buildUserMentions(rawUsers),
            [buildUserMentions, rawUsers],
        )

        const mentionUsersRefreshPromiseRef = useRef<Promise<UserMention[] | null> | null>(null)
        const lastMentionUsersRefreshAtRef = useRef(0)
        const lastMentionUsersResultRef = useRef<UserMention[] | null>(null)

        useEffect(() => {
            lastMentionUsersRefreshAtRef.current = 0
            lastMentionUsersResultRef.current = null
        }, [buildUserMentions])

        const refreshMentionUsers = useCallback(() => {
            if (!gon.currentUser) return userMentions

            const now = Date.now()
            if (
                lastMentionUsersResultRef.current &&
                now - lastMentionUsersRefreshAtRef.current < MENTION_USERS_REFRESH_COOLDOWN_MS
            ) {
                return lastMentionUsersResultRef.current
            }

            if (mentionUsersRefreshPromiseRef.current) {
                return mentionUsersRefreshPromiseRef.current
            }

            lastMentionUsersRefreshAtRef.current = now
            const refreshPromise = api
                .getUsers()
                .then(freshUsers => {
                    const freshMentions = buildUserMentions(freshUsers)
                    lastMentionUsersResultRef.current = freshMentions
                    useStore.getState().set({ users: freshUsers })
                    return freshMentions
                })
                .catch(error => {
                    console.error('[CommonEditor] Failed to refresh mention users:', error)
                    return null
                })
                .finally(() => {
                    mentionUsersRefreshPromiseRef.current = null
                })

            mentionUsersRefreshPromiseRef.current = refreshPromise
            return refreshPromise
        }, [buildUserMentions, userMentions])

        const configuredAgentModels = useMemo(
            () =>
                needsEditableEditorData
                    ? getConfiguredAgentModels(agentModelConfig)
                    : EMPTY_EDITOR_DATA,
            [agentModelConfig, needsEditableEditorData],
        )

        // Base @@ mention list now comes from the backend-owned model contract.
        const baseModelsList = useMemo(
            () =>
                configuredAgentModels.map(model => ({
                    denotationChar: '@@',
                    value: model.id,
                    rawValue: model.mentionHandle,
                    content: model.mentionHandle,
                    id: model.id,
                    name: model.mentionHandle,
                    searchableName: model.mentionHandle.toLowerCase().replace(/[^a-z0-9]/g, ''),
                    isModel: true,
                })),
            [configuredAgentModels],
        )

        // Layer in per-user OpenClaw aliases from API models.
        const aiModelsForAutocomplete = props.aiModels || storeAiModels || []
        const openclawModels = useMemo(
            () =>
                aiModelsForAutocomplete
                    .filter(model => {
                        const provider = String(model.provider || '')
                        const commandTag = String(model.actionTagName || '')
                        const name = String(model.name || '')
                        const openclawScope = String(model.openclawVisibilityScope || '')
                        const openclawTeamIds = model.openclawTeamIds || []
                        const isStreamScoped = openclawScope === 'stream'
                        const inCurrentStreamScope =
                            questTeamId &&
                            openclawTeamIds.map(id => String(id)).includes(String(questTeamId))
                        if (isStreamScoped && !inCurrentStreamScope) return false

                        return (
                            provider === 'openclaw' ||
                            commandTag.startsWith('openclaw-') ||
                            name.startsWith('openclaw-')
                        )
                    })
                    .map(model => {
                        const modelTag = model.actionTagName || model.humanName || model.name || ''
                        const displayName = model.humanName || modelTag
                        const stableModelId = String(model.id || modelTag)
                        return {
                            denotationChar: '@@',
                            value: modelTag,
                            rawValue: modelTag,
                            content: displayName,
                            id: stableModelId,
                            name: displayName,
                            searchableName: String(displayName)
                                .toLowerCase()
                                .replace(/[^a-z0-9]/g, ''),
                            isModel: true,
                        }
                    })
                    .filter(model => Boolean(model.rawValue)),
            [aiModelsForAutocomplete, questTeamId],
        )
        const modelsList = useMemo(
            () =>
                [...baseModelsList, ...openclawModels].filter(
                    (model, idx, arr) =>
                        arr.findIndex(m => String(m.id) === String(model.id)) === idx,
                ),
            [baseModelsList, openclawModels],
        )

        const resolveModelMentionDetails = useCallback(
            (modelIdentifier: string | null | undefined) => {
                const normalizedIdentifier = String(modelIdentifier || '').trim()
                if (!normalizedIdentifier) return null

                const matchedModel = modelsList.find(model => {
                    if (String(model.id) === normalizedIdentifier) return true
                    if (String(model.rawValue || '') === normalizedIdentifier) return true
                    if (String(model.value || '') === normalizedIdentifier) return true
                    return false
                })

                if (matchedModel) {
                    const mentionId = String(matchedModel.id || normalizedIdentifier)
                    const mentionContent = String(
                        matchedModel.content ||
                            matchedModel.name ||
                            matchedModel.rawValue ||
                            mentionId,
                    )
                    return {
                        id: mentionId,
                        content: mentionContent,
                        label: mentionContent,
                        link: `/models/${mentionId}`,
                    }
                }

                const mentionDetails = getAgentModelMentionDetails(
                    normalizedIdentifier,
                    agentModelConfig,
                    aiModelsForAutocomplete,
                )
                if (!mentionDetails) return null

                return {
                    id: mentionDetails.id,
                    content: mentionDetails.mentionHandle,
                    label: mentionDetails.mentionHandle,
                    link: mentionDetails.link,
                }
            },
            [agentModelConfig, aiModelsForAutocomplete, modelsList],
        )

        const actionCommandItems = useMemo(() => {
            if (props.userMentionsOnly) return []
            return buildActionCommandAutocompleteItems(
                aiModelsForAutocomplete,
                storeActionDefinitions,
            )
        }, [aiModelsForAutocomplete, props.userMentionsOnly, storeActionDefinitions])
        const shouldAutoManageModelMention =
            props.type === 'new-answer' &&
            !questModel.usesOpenclawStreamRouting(props.quest, aiModelsForAutocomplete)

        // Tiptap handles its own keyboard and mention click events internally

        // Tiptap handles setValue through its own API

        /* Quill (unhelpfully in our case) converts images pasted from the
         * clipboard into base64 encoded data urls which were polluting the db with
         * massive json objects. This function converts them to standard browser
         * file/blob objects, and then passes them to the parent's imgHandler fn
         * for normal processing */
        async function processBase64Img(base64Str) {
            if (typeof base64Str !== 'string' || base64Str.length < 100) {
                return base64Str
            }
            const blob = dataUrlToBlob(base64Str)
            const mimeType = blob.type || 'image/png'
            const extension = mimeType.split('/')[1] || 'png'
            const pastedImage = new File([blob], `pasted-image.${extension}`, {
                type: mimeType,
                lastModified: Date.now(),
            })

            // make a synthetic event to pass to the parent's imgHandler
            const pseudoEv = {
                preventDefault: () => {},
                stopPropagation: () => {},
                target: { files: [pastedImage] },
            }
            props.imgHandler(pseudoEv)
        }

        // Add text state tracking
        let lastTextState = ''
        let isProcessingTextChange = false
        let pendingFormatting = false

        // Create the debounced function once and store it in the ref
        useEffect(() => {
            // Create the debounced function
            handleLinksRef.current = debounce(
                nonKnovLinks => {
                    const editorFingerprint = generateFingerprint(nonKnovLinks)
                    const hasChanges = props.linkPreviewsFingerprint !== editorFingerprint

                    // Only update if links have actually changed
                    if (hasChanges && props.setLinkPreviews) {
                        props.setLinkPreviews(uniq(nonKnovLinks))
                    }
                },
                500,
                {
                    leading: false,
                    trailing: true,
                },
            )

            // Create debounced function for KnovQuest links
            handleKnovQuestLinksRef.current = debounce(
                async knovEmbedLinks => {
                    const answer = props.answer
                    const shortCircuit =
                        answer?.is_transcription ||
                        answer?.mp4_recording_url ||
                        answer?.recording_url
                    if (shortCircuit) return // don't do this for transcriptions

                    const knovEmbedTargetKeys = getKnovEmbedTargetKeys(knovEmbedLinks || [])
                    const editorFingerprint = generateFingerprint(knovEmbedTargetKeys)
                    const hasChanges = (props.embedsFingerprint || '') !== editorFingerprint

                    if (hasChanges) {
                        // Create promise for embed loading
                        const embedPromise = resolveKnovEmbedLinks(knovEmbedLinks || [], {
                            fetchQuest: api.getQuest,
                            fetchAnswer: api.getAnswer,
                            fetchAnonymousShareAnswer: api.getAnswerByAnonymousShareToken,
                        }).then(embeds => {
                            embeds = embeds.filter(e => !!e)
                            props?.setEmbeds?.(embeds)
                            return embeds
                        })

                        // Notify that embeds are loading if the callback is provided
                        if (props.setEmbedsLoading) {
                            props.setEmbedsLoading(true, embedPromise)
                        }

                        return embedPromise
                    }
                },
                500,
                {
                    leading: false,
                    trailing: true,
                },
            )

            // Create debounced function for Stream links
            handleStreamLinksRef.current = debounce(
                async streamEmbedLinks => {
                    const answer = props.answer
                    const shortCircuit =
                        answer?.is_transcription ||
                        answer?.mp4_recording_url ||
                        answer?.recording_url
                    if (shortCircuit) return // don't do this for transcriptions

                    const streamEmbeds = extractStreamEmbedConfigs(streamEmbedLinks || [])
                    const editorFingerprint = generateFingerprint(streamEmbeds, ',', 'id')
                    const hasChanges = props.streamEmbedsFingerprint !== editorFingerprint

                    if (hasChanges && props.setStreamEmbeds) {
                        props.setStreamEmbeds(streamEmbeds)
                    }
                    props.setStreamEmbedsLoading?.(false)
                },
                500,
                {
                    leading: false,
                    trailing: true,
                },
            )

            // Cleanup function
            return () => {
                if (handleLinksRef.current) {
                    handleLinksRef.current.cancel()
                }
                if (handleKnovQuestLinksRef.current) {
                    handleKnovQuestLinksRef.current.cancel()
                }
                if (handleStreamLinksRef.current) {
                    handleStreamLinksRef.current.cancel()
                }
            }
        }, [
            props.linkPreviewsFingerprint,
            props.embedsFingerprint,
            props.streamEmbedsFingerprint,
            props.setStreamEmbeds,
            props.setStreamEmbedsLoading,
        ])

        // Handle text changes from Quill
        async function handleTextChange(delta, oldContents, source) {
            // For Tiptap, just call the basic onChange if needed
            if (props.onChange) {
                props.onChange(delta) // Tiptap already converts to Delta
            }
        }

        // Handle key down events
        function handleKeyDown(quill, ev) {
            const enterPostsImmediatelyEnabled = useUserHasFeature('enter_key_posts_immediately')
            const hasMobileShiftEnterFeature = useUserHasFeature('mobile_shift_enter_to_post')
            const isMac = navigator.platform.includes('Mac')
            const modifierKey = isMac ? ev.metaKey : ev.ctrlKey

            // determine if this key combo should trigger a post
            let shouldSubmit = false
            if (ev.key === 'Enter') {
                if (props.type === 'search') {
                    // enter always submits while searching
                    shouldSubmit = true
                } else if (enterPostsImmediatelyEnabled) {
                    // post on enter, newline on shift+enter
                    shouldSubmit = !ev.shiftKey
                } else {
                    // Default behavior:
                    // - Desktop: cmd/ctrl+enter to post, enter for newline
                    // - Mobile/iOS: shift+enter to post (if feature enabled), enter for newline
                    const mobileShiftPost =
                        (isMobile || isIOS) && hasMobileShiftEnterFeature && ev.shiftKey
                    shouldSubmit = mobileShiftPost || (!isMobile && !isIOS && modifierKey)
                }
            }

            // handle post if conditions are met
            if (shouldSubmit && props.postHandler && props.type !== 'import') {
                props.postHandler()
                ev.preventDefault()
                ev.stopPropagation()
                return false // Return false to prevent default behavior (Quill convention)
            }
            // Don't return anything to allow default behavior
        }

        const isMobileClass = isMobile ? 'is-mobile' : ''

        const showableValue = useMemo(() => {
            if (!props.readOnly) return defaultValue
            return getReadOnlyShowableValue(defaultValue, {
                answer: props.answer,
                isEmbed: props.isEmbed,
            })
        }, [defaultValue, props.answer, props.isEmbed, props.readOnly])
        // Forward ref to the editor instance (provides Quill-compatible interface for both editors)
        useEffect(() => {
            // Expose the ref to parent components via forwardRef
            if (ref && typeof ref === 'object' && 'current' in ref) {
                // For Tiptap, provide a Quill-compatible wrapper interface
                const tiptapEditor = getTiptapEditor()
                if (tiptapEditor) {
                    // Create a Quill-compatible wrapper around the Tiptap editor
                    const tiptapWrapper = {
                        // Provide basic Quill-like methods
                        getEditor: () => tiptapEditor,
                        getContents: () =>
                            tiptapEditor.getContents ? tiptapEditor.getContents() : null,
                        getText: () => (tiptapEditor.getText ? tiptapEditor.getText() : ''),
                        getLength: () => (tiptapEditor.getText ? tiptapEditor.getText().length : 0),
                        focus: () => {
                            if (tiptapEditor.focus) {
                                return tiptapEditor.focus()
                            }
                            const actualEditor = tiptapEditor.getEditor
                                ? tiptapEditor.getEditor()
                                : tiptapEditor
                            return actualEditor?.commands?.focus?.(undefined, {
                                scrollIntoView: false,
                            })
                        },
                        hasFocus: () => {
                            if (tiptapEditor.hasFocus) return tiptapEditor.hasFocus()
                            const actualEditor = tiptapEditor.getEditor
                                ? tiptapEditor.getEditor()
                                : tiptapEditor
                            return actualEditor?.isFocused || false
                        },
                        setContents: (contents: unknown) => {
                            if (tiptapEditor.setContents) {
                                return tiptapEditor.setContents(contents)
                            }
                        },

                        clearContentProgrammatically: () => {
                            programmaticChangeFlagSetter(true) // Set flag before operation

                            // Use requestAnimationFrame to ensure we're not in the middle of another transaction
                            requestAnimationFrame(() => {
                                try {
                                    // Get the actual editor from the wrapper if needed
                                    const actualEditor = tiptapEditor.getEditor
                                        ? tiptapEditor.getEditor()
                                        : tiptapEditor

                                    if (actualEditor && actualEditor.commands) {
                                        // Use setContent with empty content instead of clearContent
                                        // This is safer and less likely to cause transaction conflicts
                                        actualEditor.commands.setContent('', { emitUpdate: false })
                                    }
                                } catch (error) {
                                    console.error(
                                        '[clearContentProgrammatically] Error clearing Tiptap content:',
                                        error,
                                    )

                                    // Fallback: try again with a delay
                                    setTimeout(() => {
                                        try {
                                            const actualEditor = tiptapEditor.getEditor
                                                ? tiptapEditor.getEditor()
                                                : tiptapEditor
                                            if (actualEditor && actualEditor.commands) {
                                                actualEditor.commands.setContent('', {
                                                    emitUpdate: false,
                                                })
                                            }
                                        } catch (retryError) {
                                            console.error(
                                                '[clearContentProgrammatically] Retry also failed:',
                                                retryError,
                                            )
                                        }
                                    }, 100)
                                }

                                // Defer resetting the flag
                                setTimeout(() => {
                                    programmaticChangeFlagSetter(false)
                                }, 0)
                            })
                        },

                        // Mark as Tiptap for consumers that need to know
                        isTiptap: true,
                        originalEditor: tiptapEditor,
                        // Read at each use, as the handle's own getter is: TipTap
                        // builds a commands object around the state of the moment
                        // it is read, so one kept from mount dispatches for a
                        // document that has since changed. Focusing the composer
                        // at its end threw ("Applying a mismatched transaction")
                        // and left the caret wherever it last stood — the start,
                        // after a draft reloaded from the modal.
                        get commands() {
                            return tiptapEditor.commands
                        },
                    }

                    // ForwardedRef can be a callback or MutableRefObject; we only assign to object refs
                    if (typeof ref !== 'function') {
                        ;(ref as React.MutableRefObject<unknown>).current = tiptapWrapper
                    }
                }
            }
        }, [ref, editorRef.current])

        // Register our custom formats before editor initialization
        useEffect(() => {
            // Add CSS class mapping to connect CSS modules with Quill's class expectations
            // This step ensures that when Quill applies its own classes, they match our CSS module classes
            const root = document.documentElement
            root.style.setProperty('--mention-class', styles.mention)
            root.style.setProperty('--model-mention-class', styles.modelMention)

            return () => {
                // Any cleanup code needed
            }
        }, []) // Empty dependency array so it only runs once

        // Synchronize editor content when defaultValue changes (e.g., socket updates)
        useEffect(() => {
            // Android IME: avoid programmatic content resets on new-answer which
            // can conflict with composition and swallow characters
            if (isAndroid && props.type === 'new-answer') return
            // Only synchronize external/default value into the editor when in read-only mode
            if (!props.readOnly) return
            if (!editorLoaded) return
            if (props.editMode) return

            if (isEqual(previousDefaultValueRef.current, defaultValue)) return

            const editorWrapper = editorRef.current as {
                setContents?: (v: unknown, s?: string) => void
                hasFocus?: () => boolean
            } | null
            if (!editorWrapper || typeof editorWrapper.setContents !== 'function') return

            // If the editor is focused and IME is composing, do not reset content
            try {
                if (editorWrapper.hasFocus && editorWrapper.hasFocus()) {
                    if (isIMEComposing && isIMEComposing()) return
                }
            } catch (e) {}

            try {
                programmaticChangeFlagSetter(true)
                editorWrapper.setContents(defaultValue, 'api')
            } finally {
                setTimeout(() => programmaticChangeFlagSetter(false), 0)
                previousDefaultValueRef.current = defaultValue
            }
        }, [defaultValue, editorLoaded, props.editMode])

        // Unified mention management effect that handles globalAgentModel changes
        useEffect(
            function handleModelMention() {
                // Don't run if editor isn't loaded yet
                if (!editorLoaded) {
                    //console.log('[handleModelMention] Editor not loaded yet, skipping...', {
                    //    editorLoaded,
                    //    hasEditorRef: !!editorRef.current,
                    //})
                    return
                }

                const editor = getTiptapEditor()
                if (!editor) {
                    console.log('[handleModelMention] No editor or wrong type', {
                        hasEditor: !!editor,
                        type: props.type,
                    })
                    return
                }

                const swiperRef = getSwiperRef()?.current
                if (!swiperRef) {
                    console.log('[handleModelMention] No swiper ref, skipping...')
                    return
                }
                if (!shouldAutoManageModelMention) {
                    return
                }

                // Use Tiptap handler
                return handleTiptapModelMention(editor, swiperRef)
            },
            [
                props.globalAgentModel,
                props.type,
                editorLoaded,
                resolveModelMentionDetails,
                shouldAutoManageModelMention,
            ], // Added editorLoaded back
        )

        // Tiptap-specific model mention handling
        // Swiper instance shape used by handleTiptapModelMention
        interface SwiperLike {
            on: (event: string, handler: () => void) => void
            off: (event: string, handler: () => void) => void
        }

        function handleTiptapModelMention(initialEditor: unknown, swiperRef: SwiperLike) {
            let isSwiping = false
            let pendingUpdate = false

            // Track swiper state
            const handleTransitionStart = () => {
                isSwiping = true
            }

            // Queue the mention update to run after swiper transition
            const handleMentionUpdate = () => {
                // If swiping, just mark that an update is pending and return
                if (isSwiping) {
                    pendingUpdate = true
                    return
                }

                // Prevent reentrant calls
                if (isProcessingModelRef.current) {
                    return
                }

                // Add delay for newly created editors to allow branch creation to stabilize
                const timeSinceCreation = Date.now() - editorCreationTimeRef.current
                if (timeSinceCreation < 500 && !hasInitializedRef.current) {
                    // If this is a very new editor and not initialized, delay processing
                    setTimeout(handleMentionUpdate, 300)
                    return
                }

                isProcessingModelRef.current = true
                pendingUpdate = false

                // Always get fresh editor instance to avoid stale state
                const currentWrapper = getTiptapEditor()

                if (!currentWrapper) {
                    console.log('[handleTiptapModelMention] No Tiptap wrapper available')
                    isProcessingModelRef.current = false
                    return
                }

                // Get the actual editor from the wrapper
                const currentEditor = currentWrapper.getEditor
                    ? currentWrapper.getEditor()
                    : currentWrapper

                if (!currentEditor || !currentEditor.commands) {
                    console.log('[handleTiptapModelMention] No Tiptap editor available')
                    isProcessingModelRef.current = false
                    return
                }

                try {
                    // Check if we have model mentions in current content
                    let currentContent
                    try {
                        currentContent = currentEditor.getJSON()
                    } catch (error) {
                        console.error('Failed to get Tiptap content:', error)
                        isProcessingModelRef.current = false
                        return
                    }

                    const hasModelMention = checkForModelMentionInTiptap(currentContent)

                    // Detect if this is a user-initiated model change
                    const isModelToggleOrChange = prevModelRef.current !== props.globalAgentModel
                    const selectedModelMention = props.globalAgentModel
                        ? resolveModelMentionDetails(props.globalAgentModel)
                        : null

                    // Determine if we should modify the editor content
                    const shouldModify =
                        isModelToggleOrChange ||
                        Boolean(props.globalAgentModel && selectedModelMention && !hasModelMention)

                    //console.log('[handleTiptapModelMention] Processing:', {
                    //currentModel: props.globalAgentModel,
                    //prevModel: prevModelRef.current,
                    //hasModelMention,
                    //isModelToggleOrChange,
                    //shouldModify,
                    //hasInitialized: hasInitializedRef.current,
                    //})

                    if (shouldModify) {
                        if (props.globalAgentModel) {
                            if (!selectedModelMention) {
                                isProcessingModelRef.current = false
                                return
                            }

                            // We have a model selected
                            try {
                                // Get fresh editor right before the operation
                                const wrapperForEdit = getTiptapEditor()
                                if (!wrapperForEdit) {
                                    throw new Error('Editor wrapper became unavailable')
                                }

                                // Get the actual Tiptap editor from the wrapper
                                const editorForEdit = wrapperForEdit.getEditor
                                    ? wrapperForEdit.getEditor()
                                    : wrapperForEdit
                                if (!editorForEdit || !editorForEdit.chain) {
                                    throw new Error(
                                        'Actual editor instance unavailable or missing chain method',
                                    )
                                }

                                // Check if there's an existing model mention with a different ID
                                const existingModelId = getExistingModelId(currentContent)
                                const needsReplacement =
                                    hasModelMention && existingModelId !== selectedModelMention.id

                                if (needsReplacement) {
                                    console.log(
                                        '[handleTiptapModelMention] Replacing existing model mention:',
                                        {
                                            oldModel: existingModelId,
                                            newModel: selectedModelMention.id,
                                        },
                                    )

                                    // Set programmatic flag to prevent deletion detection
                                    programmaticChangeFlagSetter(true)

                                    // First remove the existing mention (don't update agent model since we're replacing)
                                    removeModelMentionsFromTiptap(editorForEdit, false)

                                    // Then insert the new one
                                    const success = editorForEdit
                                        .chain()
                                        .insertContent({
                                            type: 'mention',
                                            attrs: {
                                                id: selectedModelMention.id,
                                                label: selectedModelMention.label,
                                                type: 'model',
                                                denotationChar: '@@',
                                                content: selectedModelMention.content,
                                                link: selectedModelMention.link,
                                            },
                                        })
                                        .insertContent(' ')
                                        .run()

                                    if (success) {
                                        console.log(
                                            '[handleTiptapModelMention] Successfully replaced model mention',
                                        )
                                        // Keep typing after the mention + trailing space.
                                        setTimeout(() => editorForEdit.commands.focus('end'), 0)
                                    }

                                    // Clear programmatic flag after operation
                                    setTimeout(() => programmaticChangeFlagSetter(false), 0)
                                } else if (!hasModelMention) {
                                    console.log(
                                        '[handleTiptapModelMention] Inserting model mention:',
                                        {
                                            model: selectedModelMention.id,
                                        },
                                    )

                                    // Set programmatic flag to prevent deletion detection
                                    programmaticChangeFlagSetter(true)

                                    // Use chain to ensure atomic operation
                                    const success = editorForEdit
                                        .chain()
                                        .insertContent({
                                            type: 'mention',
                                            attrs: {
                                                id: selectedModelMention.id,
                                                label: selectedModelMention.label,
                                                type: 'model',
                                                denotationChar: '@@',
                                                content: selectedModelMention.content,
                                                link: selectedModelMention.link,
                                            },
                                        })
                                        .insertContent(' ')
                                        .run()

                                    if (success) {
                                        console.log(
                                            '[handleTiptapModelMention] Successfully inserted model mention',
                                        )
                                        // Keep typing after the mention + trailing space.
                                        setTimeout(() => editorForEdit.commands.focus('end'), 0)
                                    } else {
                                        throw new Error('insertContent returned false')
                                    }

                                    // Clear programmatic flag after operation
                                    setTimeout(() => programmaticChangeFlagSetter(false), 0)
                                }
                            } catch (error) {
                                console.error('Failed to handle model mention in Tiptap:', error)

                                // Fallback: try simple text insertion with fresh editor
                                try {
                                    console.log('[handleTiptapModelMention] Trying text fallback')
                                    programmaticChangeFlagSetter(true)
                                    const wrapperForFallback = getTiptapEditor()
                                    if (wrapperForFallback) {
                                        const editorForFallback = wrapperForFallback.getEditor
                                            ? wrapperForFallback.getEditor()
                                            : wrapperForFallback
                                        if (editorForFallback && editorForFallback.commands) {
                                            editorForFallback.commands.insertContent(
                                                `@@${selectedModelMention.content} `,
                                            )
                                            setTimeout(
                                                () => editorForFallback.commands.focus('end'),
                                                0,
                                            )
                                        }
                                    }
                                    setTimeout(() => programmaticChangeFlagSetter(false), 0)
                                } catch (fallbackError) {
                                    console.error('Text fallback also failed:', fallbackError)
                                }
                            }
                        } else if (!props.globalAgentModel && hasModelMention) {
                            // Should not have mention but does - remove it
                            console.log('[handleTiptapModelMention] Removing model mentions')
                            programmaticChangeFlagSetter(true)
                            const wrapperForRemove = getTiptapEditor()
                            if (wrapperForRemove) {
                                const editorForRemove = wrapperForRemove.getEditor
                                    ? wrapperForRemove.getEditor()
                                    : wrapperForRemove
                                removeModelMentionsFromTiptap(editorForRemove)
                            }
                            setTimeout(() => programmaticChangeFlagSetter(false), 0)
                        }
                    }

                    // Always check for removal regardless of shouldModify
                    // This ensures mentions are removed when no model is selected
                    if (!props.globalAgentModel && hasModelMention && hasInitializedRef.current) {
                        // Protective measures to prevent accidental removal during branch creation
                        // But only apply them if this wasn't an explicit user action

                        // If this is a direct model toggle/change (user action), always allow removal
                        if (isModelToggleOrChange) {
                            console.log(
                                '[handleTiptapModelMention] User explicitly removed model - removing mentions',
                            )
                            programmaticChangeFlagSetter(true)
                            const wrapperForRemove = getTiptapEditor()
                            if (wrapperForRemove) {
                                const editorForRemove = wrapperForRemove.getEditor
                                    ? wrapperForRemove.getEditor()
                                    : wrapperForRemove
                                removeModelMentionsFromTiptap(editorForRemove)
                            }
                            setTimeout(() => programmaticChangeFlagSetter(false), 0)
                        } else {
                            // Only apply protective measures for non-user-initiated changes

                            // 1. Grace period - don't remove mentions if editor was just created (within 3 seconds)
                            const timeSinceCreation = Date.now() - editorCreationTimeRef.current
                            const isWithinGracePeriod = timeSinceCreation < 3000

                            // 2. Check if the editor content looks like it was just posted (short content with mention)
                            const textContent = currentEditor.getText()?.trim() || ''
                            const isLikelyBranchTransition =
                                textContent.length < 100 && // Short content
                                hasModelMention && // Has model mention
                                textContent.includes('@@') && // Contains mention syntax
                                timeSinceCreation < 5000 // And editor is relatively new

                            // 3. Check if this looks like a model mention that was recently inserted
                            const hasRecentModelMention = textContent.match(/^@@[\w-]+\s*$/m) // Model mention at start or on its own line

                            const shouldSkipRemoval =
                                isWithinGracePeriod ||
                                isLikelyBranchTransition ||
                                hasRecentModelMention

                            if (!shouldSkipRemoval) {
                                console.log(
                                    '[handleTiptapModelMention] Removing model mentions (no current model)',
                                )
                                programmaticChangeFlagSetter(true)
                                const wrapperForRemove = getTiptapEditor()
                                if (wrapperForRemove) {
                                    const editorForRemove = wrapperForRemove.getEditor
                                        ? wrapperForRemove.getEditor()
                                        : wrapperForRemove
                                    removeModelMentionsFromTiptap(editorForRemove)
                                }
                                setTimeout(() => programmaticChangeFlagSetter(false), 0)
                            } else {
                                console.log(
                                    '[handleTiptapModelMention] Skipping mention removal - protective measures triggered',
                                    {
                                        isWithinGracePeriod,
                                        isLikelyBranchTransition,
                                        hasRecentModelMention,
                                        timeSinceCreation,
                                    },
                                )
                            }
                        }
                    }

                    // Update the stored previous model value AFTER operations
                    prevModelRef.current = props.globalAgentModel

                    // Mark as initialized after first run
                    if (!hasInitializedRef.current) {
                        hasInitializedRef.current = true
                    }
                } catch (error) {
                    console.error('[handleTiptapModelMention] Error in mention processing:', error)
                } finally {
                    isProcessingModelRef.current = false
                }
            }

            const handleTransitionEnd = () => {
                isSwiping = false
                if (pendingUpdate) {
                    setTimeout(handleMentionUpdate, 50)
                }
            }

            // Set up swiper event listeners
            swiperRef.on('transitionStart', handleTransitionStart)
            swiperRef.on('transitionEnd', handleTransitionEnd)

            // Run the mention update
            handleMentionUpdate()

            // Cleanup
            return () => {
                swiperRef.off('transitionStart', handleTransitionStart)
                swiperRef.off('transitionEnd', handleTransitionEnd)
            }
        }

        // Helper function to check for model mentions in Tiptap content
        // JSON shape from Tiptap editor.getJSON()
        interface TiptapNodeJSON {
            type: string
            attrs?: Record<string, unknown>
            content?: TiptapNodeJSON[]
        }

        const checkForModelMentionInTiptap = (content: TiptapNodeJSON): boolean => {
            if (!content || !content.content) return false

            const checkNode = (node: TiptapNodeJSON): boolean => {
                if (node.type === 'mention' && node.attrs?.denotationChar === '@@') {
                    return true
                }
                if (node.content) {
                    return node.content.some(checkNode)
                }
                return false
            }

            return content.content.some(checkNode)
        }

        // Helper function to get the ID of existing model mention
        const getExistingModelId = (content: TiptapNodeJSON): string | null => {
            if (!content || !content.content) return null

            let modelId: string | null = null

            const findModelId = (node: TiptapNodeJSON): boolean => {
                if (node.type === 'mention' && node.attrs?.denotationChar === '@@') {
                    modelId = node.attrs.id as string
                    return true // Stop searching after finding first model mention
                }
                if (node.content) {
                    return node.content.some(findModelId)
                }
                return false
            }

            content.content.some(findModelId)
            return modelId
        }

        // Helper function to remove model mentions from Tiptap
        // Editor instance shape for ProseMirror state/view manipulation.
        // The actual type is a Tiptap Editor but we only use state/view/doc here.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const removeModelMentionsFromTiptap = (
            tiptapEditor: Record<string, any>,
            updateAgentModel: boolean = true,
        ) => {
            try {
                const { state } = tiptapEditor
                const { doc } = state
                let tr = state.tr
                let removedCount = 0

                // Walk through the document and find model mentions
                doc.descendants(
                    (
                        node: {
                            type: { name: string }
                            attrs?: Record<string, unknown>
                            nodeSize: number
                        },
                        pos: number,
                    ) => {
                        if (node.type.name === 'mention' && node.attrs?.denotationChar === '@@') {
                            // Remove the mention and any following space
                            const from = pos
                            const to = pos + node.nodeSize

                            // Check if there's a space after the mention
                            const nextNode = doc.nodeAt(to)
                            const spaceEnd = nextNode && nextNode.textContent === ' ' ? to + 1 : to

                            tr = tr.delete(from, spaceEnd)
                            removedCount++
                        }
                    },
                )

                if (removedCount > 0) {
                    tiptapEditor.view.dispatch(tr)

                    // Only notify parent if we're not in the middle of a replacement
                    if (updateAgentModel && props.setAgentModel) {
                        props.setAgentModel(null)
                    }
                }
            } catch (error) {
                console.error('Failed to remove model mentions from Tiptap:', error)
            }
        }

        // Function to remove all model mentions from the Quill editor

        // Don't render anything if readonly and empty
        if (props.readOnly && isEmpty(showableValue)) {
            return null
        }

        return (
            <div
                className={cn(`common-editor-comp ${edit} fs-mask`, isMobileClass, props.type)}
                data-paste-image={props.uiId}
                data-mention-editor="true"
                data-testid={props.editorTestId}
            >
                <TiptapEditor
                    ref={editorRef}
                    type={props.type}
                    defaultValue={defaultValue}
                    readOnly={props.readOnly}
                    collapseEnabled={props.collapseEnabled}
                    collapseMaxChars={props.collapseMaxChars}
                    collapseInstanceId={props.collapseInstanceId}
                    collapseShowMoreControl={props.collapseShowMoreControl}
                    placeholder={edit ? props.placeholder : ''}
                    onTextChange={handleTextChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleEditorFocus}
                    onBlur={props.onBlur}
                    onEditorLoad={quillInstance => {
                        setEditorLoaded(true)

                        // Mark as initialized when Tiptap loads
                        hasInitializedRef.current = true
                        //console.log('[TiptapEditor] Editor loaded and initialized')

                        // Handle model mention immediately when editor loads
                        if (props.globalAgentModel && shouldAutoManageModelMention) {
                            const selectedModelMention = resolveModelMentionDetails(
                                props.globalAgentModel,
                            )
                            if (!selectedModelMention) {
                                if (props.onEditorLoad) props.onEditorLoad(quillInstance)
                                return
                            }

                            console.log(
                                '[TiptapEditor] Handling model mention on load:',
                                selectedModelMention.id,
                            )

                            // Get the editor and immediately insert model mention if needed
                            const tiptapEditor = quillInstance.getEditor
                                ? quillInstance.getEditor()
                                : quillInstance
                            if (tiptapEditor && tiptapEditor.commands) {
                                // Check if we already have a model mention
                                const currentContent = tiptapEditor.getJSON()
                                const hasModelMention = checkForModelMentionInTiptap(currentContent)

                                if (!hasModelMention) {
                                    // Insert model mention immediately
                                    programmaticChangeFlagSetter(true)
                                    const success = tiptapEditor
                                        .chain()
                                        .insertContent({
                                            type: 'mention',
                                            attrs: {
                                                id: selectedModelMention.id,
                                                label: selectedModelMention.label,
                                                type: 'model',
                                                denotationChar: '@@',
                                                content: selectedModelMention.content,
                                                link: selectedModelMention.link,
                                            },
                                        })
                                        .insertContent(' ')
                                        .run()

                                    if (success) {
                                        console.log(
                                            '[TiptapEditor] Successfully inserted model mention on load',
                                        )
                                        // Keep typing after the mention + trailing space.
                                        setTimeout(
                                            () =>
                                                tiptapEditor.commands.focus('end', {
                                                    scrollIntoView: false,
                                                }),
                                            0,
                                        )
                                    }
                                    setTimeout(() => programmaticChangeFlagSetter(false), 0)
                                }
                            }
                        }

                        if (props.onEditorLoad) props.onEditorLoad(quillInstance)
                    }}
                    lastKeyPressed={lastKeyPressed}
                    programmaticChangeRef={programmaticChangeRef}
                    users={userMentions}
                    refreshMentionUsers={refreshMentionUsers}
                    models={props.userMentionsOnly ? [] : modelsList}
                    actionCommandItems={actionCommandItems}
                    getDefaultActionTokenAttrs={props.getDefaultActionTokenAttrs}
                    getActionTokenReferenceMentions={props.getActionTokenReferenceMentions}
                    onActionTokenPromptChange={props.onActionTokenPromptChange}
                    readOnlyActionTokenPrompts={props.readOnlyActionTokenPrompts}
                    setAgentModel={props.setAgentModel}
                    setLinkPreviews={props.setLinkPreviews}
                    setEmbeds={props.setEmbeds}
                    diagnosticComposer={props.diagnosticComposer}
                    diagnosticComposerId={props.diagnosticComposerId}
                    setEmbedsLoading={props.setEmbedsLoading}
                    setStreamEmbeds={props.setStreamEmbeds}
                    setStreamEmbedsLoading={props.setStreamEmbedsLoading}
                    linkPreviewsFingerprint={props.linkPreviewsFingerprint}
                    embedsFingerprint={props.embedsFingerprint}
                    streamEmbedsFingerprint={props.streamEmbedsFingerprint}
                    answer={props.answer}
                    mentionStyles={styles}
                    imgHandler={props.imgHandler}
                    videoHandler={props.videoHandler}
                    postHandler={props.postHandler}
                />
            </div>
        )
    },
)

const getTextAndEmbeds = quill => {
    if (!quill || !quill.getContents || !quill.getContents().ops) {
        return ''
    }

    return quill.getContents().ops.reduce((text, op) => {
        if (typeof op.insert === 'string') {
            // Preserve spaces exactly as they are
            return text + op.insert
        } else if (op.insert && typeof op.insert === 'object') {
            // Handle embeds and mentions
            if (op.insert.mention) {
                return text + '@'
            }
            return text + ' '
        }
        return text
    }, '')
}

// Alternative to matchAll for older ECMAScript versions
function findAllMatches(text, regex) {
    if (!regex.global) {
        regex = new RegExp(regex.source, regex.flags + 'g')
    }

    const matches = []
    let match
    while ((match = regex.exec(text)) !== null) {
        // Add index property to make it consistent with matchAll results
        matches.push(match)
    }
    return matches
}

// Updated function for meme matches handling
function processMemeMatches(quill) {
    const memeMatches = findAllMatches(getTextAndEmbeds(quill), /\[\[\s*([\w\s]+)\s*\]\]/g)

    memeMatches.forEach((val, index) => {
        const match = val[0]
        const matchWithSpaces = match.substring(2, match.length - 2)
        const memeContent = val[1]

        if (memeContent) {
            const format = quill.getFormat(val.index + 2, 1)
            const formatSecond = quill.getFormat(val.index + 3, 1) //to allow editing links from the front
            const formatLink = format?.link || formatSecond?.link

            if (
                formatLink &&
                typeof formatLink === 'string' &&
                formatLink.indexOf('_') > -1 &&
                formatLink.split('_').splice(2).join('_') !== memeContent
            ) {
                const parts = formatLink.split('_')
                quill.formatText(val.index + 2, memeContent.length, {
                    link: `${parts[0]}_${parts[1]}_${memeContent}`,
                })
                if (parts.splice(2).join('_').length < memeContent.length)
                    quill.setSelection(quill.getSelection().index + 1, 0)
            } else if (!formatLink) {
                quill.formatText(val.index + 2, matchWithSpaces.length, {
                    link: `@v2quest_self_${memeContent}`,
                })
            }
        }
    })
}

// Quill type on the export maintains backward-compatible ref shape for callers
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default React.memo(
    React.forwardRef<Quill, Record<string, unknown>>((props, ref) => (
        <ErrorBoundary label="CommonEditor">
            <CommonEditor ref={ref} {...props} />
        </ErrorBoundary>
    )),
)
